/* @flow */

import { batch } from 'react-redux';
import {
  getBraindateConversation,
  normalizeBraindate,
  normalizeBraindates,
  getBraindateId,
} from '@braindate/domain/lib/braindate/util';

import {
  getConversationId,
  getConversationUnreadNotificationsCount,
} from '@braindate/domain/lib/conversation/util';

import * as types from './braindateActionTypes';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import {
  topicBraindateEndpoint,
  braindatesEndpoint,
  feedbackEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';
import { showExpressionOfGratitudePostBraindateModal } from 'src/shared/app/expression-of-gratitude/action/expressionOfGratitudeAction';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { GetState } from 'src/shared/core/type/reduxTypes';
import type { Methods } from 'src/shared/app/base/middleware/httpMethodMiddleware';

// Braindates

export function simulateBraindatesFetch() {
  return {
    type: types.SIMULATE_BRAINDATES_FETCH,
  };
}

export function fetchBraindatesOptimally() {
  return fetchBraindates(true);
}

export function fetchBraindates(optimized: boolean = false) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchBraindatesRequest());

    const params = {};

    if (optimized) {
      params.for_braindate_screen = true;
    }
    return get(braindatesEndpoint(), params)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeBraindates(json);
        const { entities, result } = normalizedData;

        batch(() => {
          dispatch(patchEntities(entities));
          // $FlowIssue
          dispatch(fetchBraindatesSuccess(result));
        });
      })
      .catch((e) => {
        dispatch(fetchBraindatesFailure(e));

        throw e;
      });
  };
}

export function fetchBraindatesRequest() {
  return {
    type: types.FETCH_BRAINDATES_REQUEST,
  };
}

export function fetchBraindatesSuccess(result: Array<Braindate>) {
  return {
    type: types.FETCH_BRAINDATES_SUCCESS,
    result,
  };
}

export function fetchBraindatesFailure(exception: Error) {
  return {
    type: types.FETCH_BRAINDATES_FAILURE,
    exception,
  };
}

// Single braindate

export function simulateBraindateFetch() {
  return {
    type: types.SIMULATE_BRAINDATE_FETCH,
  };
}

export function fetchBraindateByTopicId(id: number) {
  return fetchBraindate(topicBraindateEndpoint(id), id);
}

export function fetchBraindate(url: (apiUrl: string) => string, id: number) {
  return (dispatch: any, _: GetState, { get }: Object) => {
    dispatch(fetchBraindateRequest({ id }));

    return get(url)
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeBraindate(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));

        dispatch(fetchBraindateSuccess({ id: result }));

        return json;
      })
      .catch((exception) => {
        dispatch(fetchBraindateFailure(exception, { id }));

        throw exception;
      });
  };
}

export function fetchBraindateRequest(action: Object) {
  return {
    type: types.FETCH_BRAINDATE_REQUEST,
    action,
  };
}

export function fetchBraindateSuccess(action: Object) {
  return {
    type: types.FETCH_BRAINDATE_SUCCESS,
    action,
  };
}

export function fetchBraindateFailure(exception: Error, action: Object) {
  return {
    type: types.FETCH_BRAINDATE_FAILURE,
    exception,
    action,
  };
}

export function setBraindateToRespondTo(braindateId: number | null) {
  return {
    type: types.SET_RESPOND_TO_BRAINDATE,
    braindateId,
  };
}

export function hideBraindateToRespondTo() {
  return {
    type: types.HIDE_RESPOND_TO_BRAINDATE,
  };
}

// Create braindate

export function createBraindate(braindate: Braindate) {
  return (dispatch: any) => {
    const normalizedData = normalizeBraindate(braindate);
    const { entities, result } = normalizedData;

    dispatch(patchEntities(entities));

    dispatch(_createBraindate(result));
  };
}

export function _createBraindate(braindateId: number) {
  return {
    type: types.CREATE_BRAINDATE,
    braindateId,
  };
}

// Delete braindate

export function deleteBraindateLocally(id: number) {
  return {
    type: types.DELETE_BRAINDATE_LOCALLY,
    id,
  };
}

// Update unread count

export function updateUnreadCount(braindate: Braindate) {
  return (dispatch: any) => {
    const conversation = getBraindateConversation(braindate);

    dispatch(
      _updateUnreadCount(
        getConversationId(conversation),
        getConversationUnreadNotificationsCount(conversation),
      ),
    );
  };
}

export function _updateUnreadCount(conversationId: number, count: ?number) {
  return {
    type: types.UPDATE_CONVERSATION_UNREAD_COUNT,
    conversationId,
    count,
  };
}

export function sendFeedback(
  braindate: Braindate,
  browser: string,
  review: Object,
): any {
  return (dispatch: any, _: GetState, { post }: Methods) =>
    post(feedbackEndpoint(getBraindateId(braindate)), {
      browser,
      answers: review,
    })
      .then(parseResponse)
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        dispatch(showExpressionOfGratitudePostBraindateModal(braindate));
      });
}
